import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { api } from "../../api"
import { TStore } from "../../store/Store"
import { getTagType } from "../../utils/utils"
import {
    BlockedReasons,
    BOQItem,
    BOQItemsPayload,
    BulkLabelPayload,
    CategoryObj,
    ChargerDetailsData,
    ChargerTypeObj,
    CPOResponseType,
    DRBoqItem,
    GetTagRequest,
    HistoryData,
    HostingAddress,
    ICountry,
    IFile,
    IResponse,
    LabelResponse,
    SlackChannel,
    SlackUser,
    StandardItems,
    TagTypes,
    UserInfo,
} from "./commonTypes"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import { getDemolition, getDemolitionTabs, resetBulkMappingStates } from "../Operations/OperationSlice"
import { getProjectsData, getProjectsTabs, getTicketDetails } from "../Projects/ProjectSlice"
import { getTicketDetails as getDemolitionTicketDetails } from "../Operations/features/Demolition/DemolitionSlice"
import { FileObj } from "../../components/MultiFileModal"
import { enqueueSnackbar } from "notistack"

const commonService = api.injectEndpoints({
    endpoints: build => ({
        getStates: build.query<{ id: number; name: string; region: string }[], void>({
            query: () => ({
                url: `states`,
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        getCities: build.query<{ id: number; name: string; state_id: number }[], { state_id: number[] }>({
            query: ({ state_id }) => ({
                url: `cities`,
                params: {
                    state_id: state_id.join(","),
                },
            }),
        }),
        getCountries: build.query<{ count: number; status: string; data: ICountry[] }, void>({
            query: () => ({
                url: `countries`,
            }),
        }),
        getCountryStates: build.query<
            { id: number; name: string; region: string }[],
            { country_id?: number; zone?: string[] }
        >({
            query: params => ({
                url: `states`,
                params:
                    params?.zone && params?.zone?.length > 0
                        ? {
                            country_id: params?.country_id ?? 101,
                            zone: JSON.stringify(params?.zone ?? []),
                        }
                        : {
                            country_id: params?.country_id ?? 101,
                        },
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        // Query to get charger details associated with a ticket
        getChargerDetails: build.query<ChargerDetailsData[], { ticket_id: string; ticket_type: string }>({
            query: params => ({
                url: `charger_details`,
                params: {
                    ticket_id: params.ticket_id,
                    ticket_type: params.ticket_type,
                },
            }),
            transformResponse: (response: IResponse<ChargerDetailsData[]>) => response.data,
        }),
        // Query to get all charger types available
        getChargersType: build.query<ChargerTypeObj[], void>({
            query: () => ({
                url: `chargers`,
            }),
        }),
        // Query to get all statuses of a particular module
        getAllStatus: build.query<{ id: number; name: string }[], { mod_id: number; sub_module: string }>({
            query: params => ({
                url: `childstatus`,
                params: params,
            }),
        }),
        // Query to fetch OEM filter options
        getOEM: build.query<IResponse<{ id: number; name: string }[]>, void>({
            query: () => ({
                url: `oem`,
            }),
        }),
        // Query to fetch CPO filter options
        getCPO: build.query<CPOResponseType, { search_key?: string; category_id?: number | "" }>({
            query: () => ({
                url: `onboarding/host/listcpo`,
            }),
        }),
        getReasons: build.query<
            { id: number; name: string; reason: string; status: boolean; reason_type: string }[],
            { module_id: number; submodule_id?: number }
        >({
            query: params => ({
                url: `settings/reasons`,
                params: {
                    ...params,
                    submodule_id: params?.submodule_id || "",
                },
            }),
            providesTags: [{ type: "Settings", id: "Reasons" }],
        }),
        getChargerMakeDeatils: build.query<any, { ticket_id: number; ticket_type?: string }>({
            query: params => ({
                url: `make`,
                params: {
                    ...params,
                },
            }),
        }),
        getStandardItems: build.query<StandardItems[], void>({
            query: () => ({
                url: `standard_items`,
            }),
        }),
        getDiscoms: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `discoms`,
            }),
        }),
        // Query to fetch assigned-to filter options
        getAssignedToFilterOptions: build.query<
            Partial<UserInfo[]>,
            { search_key?: string; ticket_id?: string; module_id?: number; submodule_id?: number | "" }
        >({
            queryFn: async (params, { getState }, extraOptions, baseQuery) => {
                const state = getState() as any
                const role_id = state?.verifyLogin?.loginData?.user_data?.role_id

                const response = await baseQuery({
                    url: `get_assign_to`,
                    params: {
                        ...params,
                        role_id: role_id ?? "",
                    },
                })

                if (response.error) {
                    return { error: response.error as FetchBaseQueryError }
                }

                return { data: response.data as Partial<UserInfo[]> }
            },
        }),
        // Query to fetch assigned-to list for progress component
        getAssignedToListOptions: build.query<
            Partial<UserInfo[]>,
            { search_key: string; ticket_id: string; module_id: number; submodule_id?: number }
        >({
            queryFn: async (params, { getState }, extraOptions, baseQuery) => {
                const state = getState() as TStore
                const role_id = state?.verifyLogin?.loginData?.user_data?.role_id

                const response = await baseQuery({
                    url: `comments/assign_to`,
                    params: {
                        ...params,
                        submodule_id: params.submodule_id ?? "",
                        role_id: role_id ?? "",
                    },
                })

                if (response.error) {
                    return { error: response.error as FetchBaseQueryError }
                }

                return { data: response.data as Partial<UserInfo[]> }
            },
        }),
        getVendorList: build.query<
            { id: number; name: string }[],
            { search_key?: string; category_id?: number | ""; team_id?: number }
        >({
            query: ({ search_key = "", category_id = "", team_id = "" }) => ({
                url: `third_party/vendor_list`,
                params: {
                    search_key,
                    category_id,
                    team_id: team_id ?? "",
                },
            }),
        }),
        getCategories: build.query<
            CategoryObj[],
            { module_id: number; is_visible?: boolean; team_id?: number; modules_flag?: boolean }
        >({
            query: params => ({
                url: `categories`,
                params: {
                    ...params,
                    is_visible: params?.is_visible ? "True" : params.module_id === 2 ? "False" : "True",
                },
            }),
        }),
        // Query to fetch charger SKUs and name, id
        getChargerSku: build.query<
            { charger_type_id: number; name: string; sku: string; oem_name: string; oem_id: number }[],
            void
        >({
            query: () => ({
                url: `get_charger_skus`,
            }),
        }),
        // Query to fetch time of day
        getTimeOfDay: build.query<IResponse<{ id: number; name: string }[]>, void>({
            query: () => ({
                url: `time_of_day`,
            }),
        }),

        getDealDoneByUsers: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `operations/deal_done_by_users`,
            }),
        }),

        getDocumentTemplates: build.query<IResponse<IFile>, { category_id: string }>({
            query: ({ category_id }) => ({
                url: `document_templates`,
                params: {
                    category_id,
                },
            }),
        }),

        // Query to get hosting address corresponding to the city and state
        getHostingAddress: build.query<HostingAddress[], { city_id?: string; state_id: string }>({
            query: ({ city_id, state_id }) => ({
                url: `hosting_address`,
                params: {
                    city_id: city_id ?? "",
                    state_id,
                },
            }),
            transformResponse: (response: IResponse<HostingAddress[]>) => response.data,
        }),

        // For Blocked reasons list
        getReasonsForBlocking: build.query<
            BlockedReasons[],
            { module_id: string; reason_type: string; submodule_id: string | number }
        >({
            query: params => ({
                url: `settings/blocked_reasons`,
                params: params,
            }),
            providesTags: ["Settings"],
        }),

        getSurveyLinkedToProject: build.query<{ survey_id: string; survey_name: string }[], { project_id: string }>({
            query: params => ({
                url: `fetch_surveys`,
                params,
            }),
            providesTags: ["Survey", "Installation", "Projects"],
        }),

        getBOQ: build.query<any, { ticket_id: string; project_id: string; boq_type: "v1" | "v2" }>({
            query: params => ({
                url: `cristi_boq`,
                params,
            }),
            transformResponse: (response: any) => (Object.keys(response)?.length > 0 ? response : undefined),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        fetchExistingBOQs: build.query<any, void>({
            query: () => ({
                url: `fetch_existing_boq`,
            }),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        getBoqGrandTotal: build.query<
            {
                grand_total: string
                items: BOQItem[]
            },
            { project_id: string; boq_type: "v1" | "v2" }
        >({
            query: params => ({
                url: `boq_grand_total`,
                params,
            }),
            transformResponse: (response: any) => (Object.keys(response)?.length > 0 ? response : undefined),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        // For ops ticket history
        getOpsHistory: build.query<HistoryData[], { ticket_id: string; sub_module: string }>({
            query: params => ({
                url: `operations/ticket_history`,
                params,
            }),
            providesTags: (result, error, arg) => [getTagType(arg.sub_module) as TagTypes],
        }),

        // Get all labels
        getAllLabels: build.query<LabelResponse, void>({
            query: () => ({
                url: `label`,
            }),
            providesTags: ["Labels"],
        }),

        // Labels for a particular ticket
        getLabels: build.query<
            LabelResponse,
            { ticket_id?: number | ""; module_id: number; submodule_id: number; search_term?: string }
        >({
            query: params => ({
                url: `label`,
                params: {
                    ...params,
                },
            }),
            providesTags: ["Labels"],
        }),

        getGrnDetails: build.query<any, { ticket_id: string; boq_id: string; item_id: string }>({
            query: params => ({
                url: `get_grn_details`,
                params,
            }),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        // Get all labels
        getAllTags: build.query<{ data: GetTagRequest[]; total_tags: number; total_pages: number }, void>({
            query: () => ({
                url: `tags/tags_operations`,
            }),
            providesTags: ["Tags"],
        }),

        getDrBoqMaterials: build.query<
            {
                items: DRBoqItem[]
            },
            { project_id: string; ticket_id?: string }
        >({
            query: params => ({
                url: `dr_boq`,
                params: {
                    ...params,
                    boq_type: "v2",
                },
            }),
        }),

        getReverseDrBoqMaterials: build.query<any[], { project_id?: string; ticket_id?: string }>({
            query: params => ({
                url: `parsed_dr`,
                params: {
                    ...params,
                    response_type: "total",
                    status: "delivered",
                },
            }),
            providesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),

        getDrCreationStatus: build.query<
            {
                create_new_ticket: boolean
                replace: {
                    flag: boolean
                    message: string
                }
            },
            { project_id: string; ticket_id: string; source_site_id: string }
        >({
            query: params => ({
                url: `/operations/check_dr_creation_status`,
                params,
            }),
        }),

        getReverseDrCreationStatus: build.query<
            {
                create_new_ticket: boolean
                replace: {
                    flag: boolean
                    message: string
                }
            },
            { project_id: string; ticket_id: string; destination_site_id: string }
        >({
            query: params => ({
                url: `/operations/check_reverse_dr_creation_status`,
                params,
            }),
        }),

        getParsedDr: build.query<
            any,
            {
                project_id?: string
                ticket_id?: string
                response_type?: "ticket_wise" | "total"
                for_demolition?: boolean
                is_reverse?: 0 | 1
            }
        >({
            query: params => ({
                url: `parsed_dr`,
                params,
            }),
            providesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),

        downloadParsedDr: build.query<
            {
                link: FileObj
            },
            {
                project_id: string
                ticket_id?: string
                response_type?: "ticket_wise" | "total"
                is_reverse?: 0 | 1
            }
        >({
            query: params => ({
                url: `download_parsed_dr`,
                params,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    window.open(data.link.urls.download, "_blank")
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.error || "Something went wrong!" }))
                }
            },
        }),

        //  map a tag
        mapTagToTicket: build.mutation<
            { msg: string; ttm_id: number },
            {
                tag_id: number
                ticket_id: number
                tag_name: string
                is_primary: number
                color_code: string
                text_color_code: string
                module: string
                user_id: number
                ticket_type?: string
                view: string
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "POST",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()

                    const { ProjectsFormValue, page, selectedValue } = state.Projects
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                    // To be removed after projects RTK integration
                    if (arg?.ticket_type === "Projects") {
                        if (arg?.view === "table") {
                            dispatch(
                                getProjectsTabs({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                            dispatch(
                                getProjectsData({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                        } else {
                            dispatch(getTicketDetails(arg.ticket_id))
                        }
                    } else if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.msg }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg?.ticket_type
                    ? [
                        getTagType(arg.ticket_type) as TagTypes,
                        "Logistics",
                        "Stations",
                        "Installation",
                        "EvApplication",
                        "Survey",
                        "Projects",
                    ]
                    : [],
        }),
        // Create a tag
        createTagToTicket: build.mutation<
            { msg: string; ttm_id: number },
            {
                tag_id: number
                ticket_id: number
                name: string
                color_code: string
                text_color_code: string
                tag_name: string
                is_primary: number
                module: string
                user_id: number
                ticket_type?: string
                view: string
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "POST",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()

                    const { ProjectsFormValue, page, selectedValue } = state.Projects
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                    // To be removed after projects RTK integration
                    if (arg?.ticket_type === "Projects") {
                        if (arg?.view === "table") {
                            dispatch(
                                getProjectsTabs({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                            dispatch(
                                getProjectsData({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                        } else {
                            dispatch(getTicketDetails(arg.ticket_id))
                        }
                    } else if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.msg }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg?.ticket_type
                    ? [
                        "Tags",
                        getTagType(arg.ticket_type) as TagTypes,
                        "Logistics",
                        "Stations",
                        "Installation",
                        "EvApplication",
                        "Survey",
                        "Projects",
                    ]
                    : ["Tags"],
        }),

        // Update primary tag
        updatePrimaryTag: build.mutation<
            { msg: string },
            {
                tag_id: number
                ticket_id: number
                tag_name: string
                is_primary: boolean
                module: string
                ticket_type: string
                user_id: number
                view: "table" | "details"
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "PUT",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()

                    const { ProjectsFormValue, page, selectedValue } = state.Projects
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                    // To be removed after projects RTK integration
                    if (arg?.ticket_type === "Projects") {
                        if (arg?.view === "table") {
                            dispatch(
                                getProjectsTabs({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                            dispatch(
                                getProjectsData({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                        } else {
                            dispatch(getTicketDetails(arg.ticket_id))
                        }
                    } else if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.msg }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                getTagType(arg.ticket_type) as TagTypes,
                "Logistics",
                "Stations",
                "Installation",
                "EvApplication",
                "Survey",
                "Projects",
            ],
        }),
        // Delete (soft delete) tag from ticket
        unmapTagFromTicket: build.mutation<
            { msg: string },
            {
                tag_id: number
                ticket_id: number
                tag_name: string
                status: 0
                module: string
                user_id: number
                ticket_type: string
                view: "table" | "details"
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "DELETE",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()

                    const { ProjectsFormValue, page, selectedValue } = state.Projects
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                    // To be removed after projects RTK integration
                    if (arg?.ticket_type === "Projects") {
                        if (arg?.view === "table") {
                            dispatch(
                                getProjectsTabs({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                            dispatch(
                                getProjectsData({ data: ProjectsFormValue, page: page, selectedValue: selectedValue })
                            )
                        } else {
                            dispatch(getTicketDetails(arg.ticket_id))
                        }
                    } else if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.msg }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                getTagType(arg.ticket_type) as TagTypes,
                "Logistics",
                "Stations",
                "Installation",
                "EvApplication",
                "Survey",
                "Projects",
            ],
        }),

        // To get the file view url by sending the presigned url
        getFileViewUrl: build.mutation<{ message: IFile }, string>({
            query: presignedUrl => ({
                url: `get_presigned_view`,
                method: "POST",
                body: {
                    url: presignedUrl,
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    const previewUrl = data.message.urls.view
                    window.open(previewUrl, "_blank")
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
        }),
        getProgressHistory: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `operations/completion_progress`,
                params: {
                    ticket_id: ticket_id,
                },
            }),
            providesTags: ["Installation"],
        }),
        updateProgressPercentage: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `operations/completion_progress`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["Installation"],
        }),
        getSlackChannels: build.query<SlackChannel[], void>({
            query: () => ({
                url: `fetch_slack_channels`,
            }),
        }),

        getSlackUsers: build.query<SlackUser[], void>({
            query: () => ({
                url: `fetch_slack_users`,
            }),
        }),

        // Mutation to change the primary tag
        updatePrimaryLabel: build.mutation<
            any,
            {
                label_id: number
                is_primary: 1 | 0
                ticket_id: number
                module_id: number
                submodule_id: number
            }
        >({
            query: body => ({
                url: `label/sequence`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Labels"],
        }),
        // Mutation to create or map labels to a particular ticket
        mapLabels: build.mutation<IResponse<string>, { data: any }>({
            query: ({ data }) => ({
                url: `label/create_or_map`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Labels"],
        }),
        // Mutation to clear labels
        clearLabels: build.mutation<IResponse<string>, { data: any }>({
            query: ({ data }) => ({
                url: `label/remove`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Labels"],
        }),

        deleteLabelMutation: build.mutation<{ message: string; status: string }, { id: number }>({
            query: ({ id }) => ({
                url: `label/${id}`,
                method: "DELETE",
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Labels"],
        }),

        // Mutation for bulk mapping to labels to multiple ticket
        updateLabelsInBulk: build.mutation<{ message: string; status: string }, BulkLabelPayload[]>({
            query: payload => ({
                url: `label/map`,
                method: "POST",
                body: {
                    data: payload,
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                    dispatch(resetBulkMappingStates())
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Labels"],
        }),

        // Mutation to update charger details for a particular ticket
        updateChargerDetails: build.mutation<
            IResponse<{ msg: string }>,
            {
                data: {
                    ticket_id: string
                    ticket_type: string
                    data: Partial<ChargerDetailsData[]>
                    seq_map?: Record<string, number>
                }
                removeCache?: boolean
            }
        >({
            query: ({ data, removeCache = true }) => ({
                url: `charger_details`,
                method: "PUT",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg?.removeCache === false
                    ? []
                    : [getTagType(arg.data.ticket_type) as TagTypes, "Logistics", "Stations", "Installation"],
        }),

        // FormData -> file, category(eg: Commissioning_Report/ logistics_bulk_upload), reference_id (charger_id in case of Commissioning_Report else ticket_id)
        uploadToS3AndGetObjUrl: build.mutation<{ error: string; timestamp: string; url: [string] }, FormData>({
            query: body => ({
                url: `upload_to_s3_and_get_obj_url`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        sendChargerAlert: build.mutation<{ message: string; success: boolean }, FormData>({
            query: body => ({
                url: `charger_alert`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        sendSlackAlert: build.mutation<
            { message: string; status: string },
            {
                ticket_id: string
                channel_id: string
                remarks: string
                user_ids: string[]
            }
        >({
            query: body => ({
                url: `notify_pod`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    if (res.data.status === "error") {
                        dispatch(setAlertFunc({ type: "error", msg: res.data.message }))
                    } else {
                        dispatch(
                            setAlertFunc({ type: "success", msg: res.data.message || "Slack alert sent successfully." })
                        )
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        addBOQ: build.mutation<
            { boq_id: number; msg: string },
            {
                project_id: number
                ticket_id: number
                reference_type: "project"
                boq_type: "v1" | "v2"
                vendor_id?: number
                module_id: number
                submodule_id: number
                items: BOQItemsPayload[]
            }
        >({
            query: body => ({
                url: `cristi_boq`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.error || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Survey", "Installation"],
        }),
        editBOQ: build.mutation<
            { boq_id: number; msg: string },
            {
                boq_id: number
                project_id: number
                ticket_id: number
                reference_type: "project"
                boq_type: "v1" | "v2"
                vendor_id?: number
                module_id: number
                submodule_id: number
                items: BOQItemsPayload[]
            }
        >({
            query: body => ({
                url: `cristi_boq`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        editBoqStatus: build.mutation<
            { msg: string },
            {
                boq_id: number
                project_id: number
                new_status: string
                boq_type: "v1" | "v2"
                is_super_admin?: boolean
            }
        >({
            query: body => ({
                url: `boq_status`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        deleteBOQ: build.mutation<
            { message: string },
            {
                boq_id: number
            }
        >({
            query: body => ({
                url: `delete_boq`,
                method: "DELETE",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        getBoqItemRemarks: build.query<
            { item_fileurl: [FileObj] },
            {
                boq_id: number
                item_id: number
            }
        >({
            query: params => ({
                url: `get_item_file_url`,
                method: "GET",
                params,
            }),
        }),

        uploadBoqItemRemarks: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `upload_item_images`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg || "File uploaded successfully" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        // Old vendor list to be removed later
        getVendorsList: build.query<
            any,
            {
                search_key: string
                page: number
                per_page: number
                service?: number[]
            }
        >({
            query: ({ search_key, page, per_page, service }) => ({
                url: `accounts/get_vendors_list`,
                params: {
                    search_key,
                    page,
                    per_page,
                    service: service?.length ? JSON.stringify(service) : "",
                },
            }),
            providesTags: ["Accounts"],
        }),

        updateGRN: build.mutation<
            { msg: string },
            FormData
        // item_id, boq_id, grn_documents, invoice_no, invoice_date
        >({
            query: body => ({
                url: `update_grn_info`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        exportIndividualBOQ: build.mutation<
            | {
                mail: {
                    msg: string
                    to: string[]
                }
            }
            | { message: string },
            {
                boq_id: number
                cta: "email" | "download"
                emails?: string[]
                username?: string
            }
        >({
            query: body => ({
                url: `export_individual_boq_excel?boq_id=${body.boq_id}`,
                method: "POST",
                body,
                responseHandler: async response => {
                    const contentType = response.headers.get("content-type")
                    if (
                        contentType &&
                        contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
                    ) {
                        const blob = await response.blob()
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement("a")
                        link.href = url
                        link.download = `BOQ_${body.boq_id}.xlsx`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                        return { message: "File downloaded successfully" }
                    }
                    return response.json()
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res: any = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: res.data?.message
                                ? res.data.message
                                : `${res.data.mail.msg} ${res.data.mail.to.join(", ")}`,
                        })
                    )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        exportBOQ: build.mutation<
            {
                mail:
                | {
                    msg: string
                    to: string[]
                }
                | { message: string }
            },
            {
                project_id: number
                cta: "export" | "download"
                boq_type: "v1" | "v2"
                ticket_id?: number
                emails?: string[]
                username?: string
            }
        >({
            query: body => ({
                url: `export_boq_grand_total`,
                method: "POST",
                body,
                responseHandler: async response => {
                    const contentType = response.headers.get("content-type")
                    if (
                        contentType &&
                        contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
                    ) {
                        const blob = await response.blob()
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement("a")
                        link.href = url
                        link.download = `BOQ_${body.boq_type}_${body.project_id}.xlsx`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                        return { message: "File downloaded successfully" }
                    }
                    return response.json()
                },
            }),
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    const res: any = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: res.data?.message
                                ? res.data.message
                                : `${res.data.mail.msg} ${res.data.mail.to.join(", ")}`,
                        })
                    )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        bulkUploadBOQ: build.mutation<
            { parsed_items: any[] },
            FormData
        // File
        >({
            query: body => ({
                url: `boq_bulk_upload`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("File uploaded successfully.", { variant: "success", autoHideDuration: 3000 })
                } catch (error: any) {
                    console.log(error)
                    enqueueSnackbar(error?.error?.data?.error, { variant: "error", autoHideDuration: 3000 })
                }
            },
        }),

        bulkUpdateDrBoqMaterials: build.mutation<
            {
                message: string
            },
            FormData
        >({
            query: body => ({
                url: `/operations/upload_dr_v3`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),

        addReverseDrBoqMaterials: build.mutation<
            {
                message: string
            },
            FormData
        >({
            query: body => ({
                url: `/operations/reverse_logistics`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),
        getComments: build.query<any, any>({
            query: (params: any) => ({
                url: `/comments/comment`,
                params: params,
            }),
            providesTags: [{ type: "Installation", id: "ticketUpadte" }],
        }),
        addComments: build.mutation<any, any>({
            query: body => ({
                url: `/comments/comment`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: [{ type: "Installation", id: "ticketUpadte" }],
        }),
    }),
})

export const {
    useLazyGetChargerDetailsQuery,
    useLazyGetChargerMakeDeatilsQuery,
    useGetStatesQuery,
    useGetCitiesQuery,
    useGetCountriesQuery,
    useGetChargersTypeQuery,
    useGetAllStatusQuery,
    useGetAllTagsQuery,
    useGetAssignedToFilterOptionsQuery,
    useGetAssignedToListOptionsQuery,
    useGetVendorListQuery,
    useLazyGetVendorsListQuery,
    useGetDiscomsQuery,
    useGetCategoriesQuery,
    useGetChargerSkuQuery,
    useGetReasonsQuery,
    useGetTimeOfDayQuery,
    useGetSurveyLinkedToProjectQuery,
    useGetOEMQuery,
    useGetStandardItemsQuery,
    useLazyGetGrnDetailsQuery,
    useLazyGetBoqItemRemarksQuery,
    useGetCPOQuery,
    useGetBOQQuery,
    useLazyGetDrBoqMaterialsQuery,
    useFetchExistingBOQsQuery,
    useGetBoqGrandTotalQuery,
    useGetDocumentTemplatesQuery,
    useGetReasonsForBlockingQuery,
    useGetOpsHistoryQuery,
    useGetCommentsQuery,
    useGetAllLabelsQuery,
    useLazyGetCountryStatesQuery,
    useGetCountryStatesQuery,
    useGetSlackUsersQuery,
    useGetSlackChannelsQuery,
    useLazyGetLabelsQuery,
    useUpdatePrimaryLabelMutation,
    useGetDealDoneByUsersQuery,
    useDeleteLabelMutationMutation,
    useUpdateLabelsInBulkMutation,
    useUpdateChargerDetailsMutation,
    useMapLabelsMutation,
    useClearLabelsMutation,
    useGetFileViewUrlMutation,
    useLazyGetHostingAddressQuery,
    useUploadToS3AndGetObjUrlMutation,
    useSendChargerAlertMutation,
    useMapTagToTicketMutation,
    useUpdatePrimaryTagMutation,
    useUnmapTagFromTicketMutation,
    useCreateTagToTicketMutation,
    useSendSlackAlertMutation,
    useExportBOQMutation,
    useAddBOQMutation,
    useEditBOQMutation,
    useEditBoqStatusMutation,
    useDeleteBOQMutation,
    useGetProgressHistoryQuery,
    useUpdateProgressPercentageMutation,
    useUpdateGRNMutation,
    useExportIndividualBOQMutation,
    useBulkUploadBOQMutation,
    useUploadBoqItemRemarksMutation,
    useBulkUpdateDrBoqMaterialsMutation,
    useGetParsedDrQuery,
    useLazyDownloadParsedDrQuery,
    useLazyGetDrCreationStatusQuery,
    useLazyGetReverseDrCreationStatusQuery,
    useLazyGetReverseDrBoqMaterialsQuery,
    useAddReverseDrBoqMaterialsMutation,
    useAddCommentsMutation
} = commonService
